import { useState } from "react";
import {
  IconUser,
  IconLock,
  IconSun,
  IconMoon,
  IconEye,
  IconEyeOff,
  IconBoxes,
  IconPercent,
  IconShield,
} from "../icons";
import type { Theme } from "../theme";
import logoBranca from "../assets/logo-branca.png";
import logoVerde from "../assets/logo-verde.png";

interface Props {
  onLogin: (user: string) => void;
  theme: Theme;
  onToggleTheme: () => void;
}

const HIGHLIGHTS = [
  { Icon: IconBoxes, text: "Saldo e valor de estoque em tempo real" },
  { Icon: IconPercent, text: "Acurácia, divergências e lead time sob controle" },
  { Icon: IconShield, text: "Dados centralizados e seguros" },
];

/**
 * Tela de login corporativa da SERCAMP — layout em duas colunas:
 * painel de marca (verde, com cena animada) à esquerda e formulário à direita.
 * Autenticação validada no back-end (POST /api/login).
 */
export default function Login({ onLogin, theme, onToggleTheme }: Props) {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ usuario: user.trim(), senha: pass }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.error ?? "Usuário ou senha inválidos.");
        setLoading(false);
        return;
      }
      onLogin(body?.user?.usuario ?? user.trim());
    } catch {
      setError("Não foi possível conectar ao servidor.");
      setLoading(false);
    }
  }

  return (
    <div className="login">
      {/* ---------- Painel de marca + cena animada ---------- */}
      <aside className="login-aside">
        <div className="login-aside-top">
          <img src={logoBranca} alt="SERCAMP" className="login-aside-logo" />
        </div>

        <div className="login-aside-mid">
          <span className="login-badge">+ de 30 anos de mercado</span>
          <h2>Sistema de Almoxarifado</h2>
          <p>
            Plataforma de gestão de estoque da SERCAMP para controlar itens,
            movimentações, inventários, separações e fornecedores em um só lugar.
          </p>

          <ul className="login-highlights">
            {HIGHLIGHTS.map((h) => (
              <li key={h.text}>
                <span className="lh-icon">
                  <h.Icon size={18} />
                </span>
                {h.text}
              </li>
            ))}
          </ul>
        </div>

        <div className="login-aside-foot">
          © 2026 SERCAMP · Todos os direitos reservados
        </div>
      </aside>

      {/* ---------- Painel do formulário ---------- */}
      <main className="login-main">
        <button
          className="theme-toggle login-theme"
          onClick={onToggleTheme}
          title={theme === "dark" ? "Tema claro" : "Tema escuro"}
          type="button"
        >
          {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
        </button>

        <div className="login-form-wrap">
          <img
            src={theme === "dark" ? logoBranca : logoVerde}
            alt="SERCAMP"
            className="login-mobile-logo"
          />

          <h1 className="login-title">Bem-vindo de volta</h1>
          <p className="login-sub">Acesse o Almoxarifado da SERCAMP</p>

          <form onSubmit={submit} className="login-form">
            <label className="login-field">
              <span>Usuário ou e-mail</span>
              <div className="input-icon">
                <IconUser size={18} />
                <input
                  autoFocus
                  placeholder="Digite seu usuário ou e-mail"
                  value={user}
                  onChange={(e) => setUser(e.target.value)}
                  autoComplete="username"
                />
              </div>
            </label>

            <label className="login-field">
              <span>Senha</span>
              <div className="input-icon">
                <IconLock size={18} />
                <input
                  type={showPass ? "text" : "password"}
                  placeholder="Digite sua senha"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="pass-toggle"
                  onClick={() => setShowPass((v) => !v)}
                  title={showPass ? "Ocultar senha" : "Mostrar senha"}
                  tabIndex={-1}
                >
                  {showPass ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                </button>
              </div>
            </label>

            {error && <div className="login-error">{error}</div>}

            <button type="submit" className="btn-primary login-btn" disabled={loading}>
              {loading ? "Entrando..." : "Entrar"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
